import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  formatUserName,
  formatCoachName,
  formatRole,
  getRoleColorClasses,
  getEngagementLevel,
  daysSinceLastActivity
} from '../../../../services/userProfileService';
import EditProfileModal from './modals/EditProfileModal';

const UserHeader = ({
  user,
  currentUser,
  userRole,
  callStats,
  memoryStats,
  onUserUpdate,
  onBackToList,
  onRefresh,
  viewMode = 'admin'
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  if (!user) {
    return (
      <div className="mb-6">
        <div className="bg-gray-100 rounded-lg p-6">
          <div className="text-gray-500">Loading user information...</div>
        </div>
      </div>
    );
  }

  // Calculate engagement metrics
  const engagement = getEngagementLevel(callStats || {}, memoryStats || {});
  const daysSinceActivity = daysSinceLastActivity(
    callStats?.last_call_date, 
    memoryStats?.latest_memory_date
  );

  // Avatar initials
  const getInitials = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    const name = user.full_name || user.email || 'U';
    return name.substring(0, 2).toUpperCase();
  };

  // Format last activity
  const formatLastActivity = () => {
    if (daysSinceActivity === null) return 'No activity recorded';
    if (daysSinceActivity === 0) return 'Active today';
    if (daysSinceActivity === 1) return 'Active yesterday';
    return `Last active ${daysSinceActivity} days ago`;
  };

  // Check if current user can edit this profile
  const canEdit = () => {
    return userRole === 'SUPER_ADMIN' || userRole === 'COACH' || currentUser?.id === user.id;
  };

  // Handle edit profile modal
  const handleEditProfile = () => {
    setShowEditModal(true);
    setShowActions(false);
  };

  const handleEditSave = (updatedUserData) => {
    // Update parent component with new user data
    if (onUserUpdate) {
      onUserUpdate(updatedUserData);
    }
    setShowEditModal(false);
    
    // Refresh the page data to get latest information
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
  };

  // Quick actions
  const quickActions = [
    {
      id: 'edit',
      label: 'Edit Profile',
      icon: '✏️',
      action: handleEditProfile,
      show: canEdit()
    },
    {
      id: 'schedule',
      label: 'Schedule Contact',
      icon: '📅',
      action: () => {
        toast('Coming soon!', {
          icon: '📅',
          duration: 2000
        });
      },
      show: userRole === 'COACH' || userRole === 'SUPER_ADMIN'
    },
    {
      id: 'message',
      label: 'Send Message',
      icon: '💬',
      action: () => {
        toast('Coming soon!', {
          icon: '💬',
          duration: 2000
        });
      },
      show: userRole === 'COACH' || userRole === 'SUPER_ADMIN'
    },
    {
      id: 'export',
      label: 'Export Data',
      icon: '📊',
      action: () => {
        toast('Coming soon!', {
          icon: '📊',
          duration: 2000
        });
      },
      show: userRole === 'SUPER_ADMIN'
    }
  ].filter(action => action.show);

  return (
    <div className="mb-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Back button — hidden in client mode */}
        {viewMode !== 'client' && (
          <div className="px-6 py-3 border-b border-gray-200">
            <button
              onClick={onBackToList}
              className="flex items-center text-gray-600 hover:text-gray-800 text-sm font-medium"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to User List
            </button>
          </div>
        )}

        {/* Main header content */}
        <div className="px-4 py-4 sm:px-6 sm:py-6">
          {/* Mobile Layout */}
          <div className="block sm:hidden">
            <div className="flex items-start justify-between mb-4">
              {/* Avatar and basic info */}
              <div className="flex items-start space-x-3 flex-1 min-w-0">
                <div className="relative flex-shrink-0">
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={formatUserName(user)}
                      className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-lg font-bold shadow-lg">
                      {getInitials(user)}
                    </div>
                  )}
                  
                  <div className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white shadow-sm ${
                    engagement.level === 'high' ? 'bg-green-500' :
                    engagement.level === 'medium' ? 'bg-yellow-500' :
                    engagement.level === 'low' ? 'bg-orange-500' : 'bg-gray-500'
                  }`} title={`Engagement: ${engagement.label}`}></div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <h1 className="text-xl font-bold text-gray-900 truncate">
                        {formatUserName(user)}
                      </h1>
                      <div className="flex items-center space-x-2 mt-1 mb-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColorClasses(user.role)}`}>
                          {formatRole(user.role)}
                        </span>
                        <span className={`text-xs text-${engagement.color}-600 font-medium`}>
                          {engagement.label}
                        </span>
                      </div>
                    </div>
                    
                    {/* Mobile actions */}
                    <div className="flex items-center space-x-2 ml-2">
                      {viewMode !== 'client' && (
                        <button
                          onClick={onRefresh}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                          title="Refresh"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      )}

                      {(viewMode === 'client' || userRole === 'COACH') ? (
                        canEdit() && (
                          <button
                            onClick={handleEditProfile}
                            className="px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 whitespace-nowrap"
                          >
                            Edit Profile
                          </button>
                        )
                      ) : (
                        quickActions.length > 0 && (
                          <div className="relative">
                            <button
                              onClick={() => setShowActions(!showActions)}
                              className="px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 whitespace-nowrap"
                            >
                              Actions
                              <svg className="ml-1 w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>

                            {showActions && (
                              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                                <div className="py-1">
                                  {quickActions.map((action) => (
                                    <button
                                      key={action.id}
                                      onClick={() => {
                                        action.action();
                                        setShowActions(false);
                                      }}
                                      className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                      <span className="mr-3 text-base">{action.icon}</span>
                                      {action.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    <div className="truncate">{user.email || 'No email'}</div>
                    <div className="text-xs">{formatLastActivity()}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden sm:block">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-6">
                {/* Avatar */}
                <div className="relative">
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={formatUserName(user)}
                      className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                      {getInitials(user)}
                    </div>
                  )}
                  
                  <div className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 border-white shadow-sm ${
                    engagement.level === 'high' ? 'bg-green-500' :
                    engagement.level === 'medium' ? 'bg-yellow-500' :
                    engagement.level === 'low' ? 'bg-orange-500' : 'bg-gray-500'
                  }`} title={`Engagement: ${engagement.label}`}></div>
                </div>

                {/* User info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <h1 className="text-2xl font-bold text-gray-900 truncate">
                      {formatUserName(user)}
                    </h1>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleColorClasses(user.role)}`}>
                      {formatRole(user.role)}
                    </span>
                  </div>

                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex items-center space-x-4">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                        {user.email || 'No email'}
                      </span>
                      
                      {user.coach && (
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          Coach: {formatCoachName(user.coach)}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center space-x-4">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formatLastActivity()}
                      </span>

                      <span className={`flex items-center text-${engagement.color}-600`}>
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        {engagement.label} Engagement
                      </span>
                    </div>

                    <div className="text-xs text-gray-500 mt-2">
                      Member since {new Date(user.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop action buttons */}
              <div className="flex items-center space-x-3">
                {viewMode !== 'client' && (
                  <button
                    onClick={onRefresh}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    title="Refresh data"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                )}

                {(viewMode === 'client' || userRole === 'COACH') ? (
                  canEdit() && (
                    <button
                      onClick={handleEditProfile}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Edit Profile
                    </button>
                  )
                ) : (
                  quickActions.length > 0 && (
                    <div className="relative">
                      <button
                        onClick={() => setShowActions(!showActions)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Quick Actions
                        <svg className="ml-2 -mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {showActions && (
                        <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                          <div className="py-1">
                            {quickActions.map((action) => (
                              <button
                                key={action.id}
                                onClick={() => {
                                  action.action();
                                  setShowActions(false);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <span className="mr-3">{action.icon}</span>
                                {action.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick stats bar - hidden for client dashboard */}
        {viewMode !== 'client' && (
          <div className="border-t border-gray-200 bg-gray-50 px-4 py-4 sm:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div className="text-center">
                <div className="text-lg sm:text-xl font-semibold text-gray-900">
                  {callStats?.total_calls || 0}
                </div>
                <div className="text-xs text-gray-600">Total Contacts</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-xl font-semibold text-gray-900">
                  {memoryStats?.total_memories || 0}
                </div>
                <div className="text-xs text-gray-600">Memories</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-xl font-semibold text-gray-900">
                  {callStats?.success_rate || 0}%
                </div>
                <div className="text-xs text-gray-600">Success Rate</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-xl font-semibold text-gray-900">
                  {callStats?.calls_this_week || 0}
                </div>
                <div className="text-xs text-gray-600">Contacts This Week</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close dropdown */}
      {showActions && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowActions(false)}
        />
      )}

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={showEditModal}
        user={user}
        currentUser={currentUser}
        userRole={userRole}
        onSave={handleEditSave}
        onCancel={handleEditCancel}
        loading={isUpdating}
      />
    </div>
  );
};

export default UserHeader;